import {
  ClientMessage, ServerMessage, InputAction,
  RoomMode, SpeedKey, Team,
} from '@shared/protocol'

const SERVER_URL = import.meta.env.VITE_SERVER_URL ?? 'wss://mean-bean-server.immoo360.workers.dev'

export interface ConnectionConfig {
  roomId:      string
  playerId:    string
  displayName: string
  team:        Team
  mode:        RoomMode
  speed:       SpeedKey
}

type MessageHandler = (msg: ServerMessage) => void
type StatusHandler  = (status: 'connecting' | 'connected' | 'disconnected') => void

export class ServerConnection {
  private ws:      WebSocket | null = null
  private onMsg:   MessageHandler
  private onStatus: StatusHandler
  private tickInterval: number | null = null
  private pendingInputs: InputAction[] = []

  constructor(onMessage: MessageHandler, onStatus: StatusHandler) {
    this.onMsg    = onMessage
    this.onStatus = onStatus
  }

  connect(cfg: ConnectionConfig): void {
    const url = new URL(`${SERVER_URL}/ws/room/${cfg.roomId}`)
    url.searchParams.set('playerId',    cfg.playerId)
    url.searchParams.set('displayName', cfg.displayName)
    url.searchParams.set('team',        cfg.team)
    url.searchParams.set('mode',        cfg.mode)
    url.searchParams.set('speed',       cfg.speed)

    this.onStatus('connecting')
    this.ws = new WebSocket(url.toString())

    this.ws.onopen = () => {
      this.onStatus('connected')
      this.startTickLoop()
    }

    this.ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as ServerMessage
        this.onMsg(msg)
      } catch (e) {
        console.error('Failed to parse server message', e)
      }
    }

    this.ws.onclose = () => {
      this.onStatus('disconnected')
      this.stopTickLoop()
    }

    this.ws.onerror = (err) => {
      console.error('WebSocket error', err)
      this.onStatus('disconnected')
    }
  }

  sendReady(): void {
    this.send({ type: 'ready' })
  }

  sendInput(action: InputAction): void {
    this.send({ type: 'input', action })
  }

  disconnect(): void {
    this.stopTickLoop()
    this.ws?.close()
    this.ws = null
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  // ─── 30 Hz tick loop — keeps server physics advancing even when idle ────────
  private startTickLoop(): void {
    this.tickInterval = window.setInterval(() => {
      if (this.connected) this.send({ type: 'tick' })
    }, 33)
  }

  private stopTickLoop(): void {
    if (this.tickInterval !== null) {
      clearInterval(this.tickInterval)
      this.tickInterval = null
    }
  }

  private send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg))
    }
  }
}
